/**
 * Unified check-in entry from Working Documents (downloaded OneDrive files only).
 * Tabs reuse CheckInNew and CheckinNewRevision; default tab is New Revision.
 * Downloaded + checked-out files open on New Document with New Revision disabled.
 */
import React, { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Stack } from "@fluentui/react";
import Box from "@mui/material/Box";
import { TeamsFxContext } from "../../components/Context";
import { useTheme } from "../../theme/OAS365Theme";
import { useTools } from "../../redux/hooks/useTools";
import { useCheckin } from "../../redux/hooks/useCheckin";
import { APP_ROUTES } from "../../routes/routeConfig";
import {
  CHECKIN_NEW,
  CHECKIN_NEW_PAGE_NAME,
  CHECKIN_NEW_REVISION,
  DATA_LOAD_STATUS,
  PAGE_NAME,
  THEME_MODE,
  CHECKIN_FORM_LAYOUT_MIN_WIDTH_PX,
} from "../../redux/constants/metaData";
import CheckInNew from "../check-in-new/components/CheckInNew";
import CheckinNewRevision from "../check-in-new-revision/components/CheckinNewRevision";

const SEGMENT_GAP_PX = 8;
const SEGMENT_MIN_HEIGHT_PX = 38;
const SEGMENT_RADIUS_PX = 5;

function TabPanel({ children, value, index }) {
  const isActive = value === index;
  if (!isActive) {
    return null;
  }
  return (
    <Box
      role="tabpanel"
      sx={{
        flex: 1,
        minHeight: 0,
        flexDirection: "column",
        overflow: "hidden",
        display: "flex",
      }}
      id={`working-doc-checkin-tabpanel-${index}`}
    >
      {children}
    </Box>
  );
}

export default function WorkingDocumentCheckInPage() {
  const theme = useTheme();
  const { themeString } = useContext(TeamsFxContext);
  const location = useLocation();
  const navigate = useNavigate();
  const tools = useTools();
  const { uploadFiles } = useCheckin();
  const selectedDocument = location.state?.selectedDocument;
  const disableNewRevisionTab = location.state?.disableNewRevisionTab === true;
  const resolveInitialTab = () => {
    if (disableNewRevisionTab) return 0;
    if (location.state?.initialTab === 0 || location.state?.initialTab === 1) {
      return location.state.initialTab;
    }
    return 1;
  };
  const [tab, setTab] = useState(resolveInitialTab);
  const revisionTabInitializedRef = useRef(false);
  const newDocTabInitializedRef = useRef(false);

  const activeGreen =
    theme.colors?.themeDarkColor ?? theme.colors?.themeColor ?? "#249f5d";
  const inactiveBorder =
    theme.colors?.Addovation20Grey ?? theme.colors?.borderColor ?? "#d8d8d8";
  const inactiveText = theme.colors?.fontColor ?? "#111827";
  const inactiveTabLabelColor =
    themeString === THEME_MODE.dark ? "#000000" : inactiveText;

  const {
    onDocTitleChange,
    onFileUploadChange,
    onDocCheckinTypeChange,
    onSelectedPageChange,
    onCheckinPageNameChanged,
    onBasicDataProgressChange,
    onNewRevisionInfoChange,
    onObjectConnectionDataLoad,
  } = tools;

  useEffect(() => {
    if (!selectedDocument) {
      navigate(APP_ROUTES.workingDocuments, { replace: true });
    }
  }, [selectedDocument, navigate]);

  useEffect(() => {
    if (disableNewRevisionTab && tab !== 0) {
      setTab(0);
    }
  }, [disableNewRevisionTab, tab]);

  useEffect(() => {
    if (tab === 0) {
      if (!selectedDocument) return;
      onDocCheckinTypeChange(CHECKIN_NEW);
      onSelectedPageChange(PAGE_NAME.home);
      const fileName = selectedDocument.name ?? "";
      const fileType = selectedDocument.fileType ?? "";
      const currentFile = Array.isArray(uploadFiles) ? uploadFiles[0] : null;
      const shouldRehydrateSelectedFile =
        currentFile?.name !== fileName || currentFile?.type !== fileType;

      if (shouldRehydrateSelectedFile) {
        onFileUploadChange(
          [
            {
              name: fileName,
              type: fileType,
              size: "0 KB",
              base64: "",
              base64str: "",
            },
          ],
          1,
        );
      }

      if (!newDocTabInitializedRef.current) {
        newDocTabInitializedRef.current = true;
        onObjectConnectionDataLoad(DATA_LOAD_STATUS.onStart);
        const title =
          selectedDocument.name?.substring(
            0,
            selectedDocument.name?.indexOf(".") ?? selectedDocument.name?.length,
          ) ?? "";
        onDocTitleChange(title);
        onCheckinPageNameChanged(CHECKIN_NEW_PAGE_NAME.basic);
        onBasicDataProgressChange(DATA_LOAD_STATUS.onLoad);
      }
    } else {
      onDocCheckinTypeChange(CHECKIN_NEW_REVISION);
      if (!revisionTabInitializedRef.current) {
        revisionTabInitializedRef.current = true;
        onNewRevisionInfoChange(DATA_LOAD_STATUS.refresh);
      }
      onSelectedPageChange(PAGE_NAME.home);
    }
  }, [
    tab,
    selectedDocument,
    onDocTitleChange,
    onFileUploadChange,
    onDocCheckinTypeChange,
    onSelectedPageChange,
    onCheckinPageNameChanged,
    onBasicDataProgressChange,
    onNewRevisionInfoChange,
    onObjectConnectionDataLoad,
    uploadFiles,
  ]);

  if (!selectedDocument) {
    return null;
  }

  const segmentBase = {
    flex: 1,
    minHeight: SEGMENT_MIN_HEIGHT_PX,
    maxHeight: SEGMENT_MIN_HEIGHT_PX,
    borderRadius: `${SEGMENT_RADIUS_PX}px`,
    fontFamily: "Poppins, sans-serif",
    fontSize: 12,
    cursor: "pointer",
    borderStyle: "solid",
    borderWidth: 1,
    outline: "none",
    transition: "background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease",
  };

  return (
    <Stack
      enableScopedSelectors
      styles={{
        root: {
          background: theme.colors.backgroundColor,
          overflow: "hidden",
          height: "100%",
          display: "flex",
          flexDirection: "column",
        },
      }}
      tokens={{ childrenGap: 0, padding: 0 }}
    >
      <Stack.Item
        grow
        styles={{
          root: {
            flex: "1 1 auto",
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflowX: "auto",
            overflowY: "hidden",
            WebkitOverflowScrolling: "touch",
          },
        }}
      >
        <div
          style={{
            minWidth: CHECKIN_FORM_LAYOUT_MIN_WIDTH_PX,
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            flex: "1 1 auto",
            minHeight: 0,
          }}
        >
          <Stack
            grow
            styles={{
              root: {
                height: "calc(100vh - 100px)",
                background: theme.colors.backgroundColor,
                padding: 0,
                margin: "0 300px",
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                flex: "1 1 auto",
                overflow: "hidden",
              },
            }}
          >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  width: "100%",
                  flexShrink: 0,
                  paddingTop: "15px",
                }}
              >
                <Box
                  role="tablist"
                  aria-label="Check-in type"
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "stretch",
                    width: "100%",
                    maxWidth: "100%",
                    gap: `${SEGMENT_GAP_PX}px`,
                  }}
                >
                  <button
                    type="button"
                    role="tab"
                    id="working-doc-checkin-tab-0"
                    aria-selected={tab === 0}
                    onClick={() => setTab(0)}
                    style={{
                      ...segmentBase,
                      backgroundColor: tab === 0 ? activeGreen : "#ffffff",
                      color: tab === 0 ? "#ffffff" : inactiveTabLabelColor,
                      fontWeight: tab === 0 ? 700 : 400,
                      borderColor: tab === 0 ? activeGreen : inactiveBorder,
                    }}
                  >
                    New Document
                  </button>
                  <button
                    type="button"
                    role="tab"
                    id="working-doc-checkin-tab-1"
                    aria-selected={tab === 1}
                    aria-disabled={disableNewRevisionTab}
                    disabled={disableNewRevisionTab}
                    onClick={() => {
                      if (!disableNewRevisionTab) setTab(1);
                    }}
                    style={{
                      ...segmentBase,
                      backgroundColor: tab === 1 ? activeGreen : "#ffffff",
                      color: tab === 1 ? "#ffffff" : inactiveTabLabelColor,
                      fontWeight: tab === 1 ? 700 : 400,
                      borderColor: tab === 1 ? activeGreen : inactiveBorder,
                      cursor: disableNewRevisionTab ? "not-allowed" : "pointer",
                      opacity: disableNewRevisionTab ? 0.45 : 1,
                    }}
                  >
                    New Revision
                  </button>
                </Box>
              </Box>
              <TabPanel value={tab} index={0}>
                <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
                  <CheckInNew />
                </Box>
              </TabPanel>
              <TabPanel value={tab} index={1}>
                <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
                  <CheckinNewRevision />
                </Box>
              </TabPanel>
            </Stack>
        </div>
      </Stack.Item>
    </Stack>
  );
}
